import { Injector, inject } from '@angular/core';
import {
  HttpErrorResponse, HttpInterceptorFn,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, delay, of, switchMap, take, throwError } from 'rxjs';
import { DevAuthService } from 'src/app/core/auth/dev-auth.service';
import { SessionRevokedService, isSessionRevokedBody } from 'src/app/core/services/session-revoked.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { environment } from 'src/environments/environment';

/**
 * Attaches the Auth0 JWT to requests against Portal.HttpFunctions (token management).
 * Requests to environment.devApiUrl (the Playground's target — Portal.DevFunctions)
 * are deliberately NOT touched here: those carry whichever gmpat_ token the user
 * picked in the Playground UI as an Authorization header set explicitly by that
 * component, not the logged-in user's own JWT — see PlaygroundPage.
 */
// Module-scoped (not per-call) so it keeps guarding against multiple simultaneous 401s
// triggering repeat redirects for the lifetime of the app, matching the previous
// class-based interceptor's instance field.
let redirecting = false;

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const auth = inject(DevAuthService);
  const toast = inject(ToastService);
  const router = inject(Router);
  // Looked up only when a session_revoked error arrives, never when the interceptor runs: the service needs the
  // TranslateService, whose loader needs HttpClient, which builds this interceptor chain. Injecting it up front is a
  // circular dependency (NG0200) that stops every request of the app (seen in My-SPA and Developer-SPA).
  const injector = inject(Injector);

  if (!request.url.startsWith(environment.apiUrl)) {
    return next(request);
  }

  const handleError = (err: HttpErrorResponse) => {
    switch (true) {
      // Session ended elsewhere: the token is still valid, so login() would sign straight back in.
      case err.status === 401 && isSessionRevokedBody(err.error):
        void injector.get(SessionRevokedService).handle();
        break;
      case err.status === 401:
        if (!redirecting) { redirecting = true; auth.login(); }
        break;
      case err.status === 0:
        toast.error('Keine Verbindung zum Server.');
        break;
      case err.status === 403:
        // Every environment.apiUrl route (/api/developer/...) requires the same
        // Entwicklerplattform permission — a 403 here always means "no Developer
        // Platform access at all", never a narrower per-action restriction. Consistent
        // with this app's server-is-source-of-truth model (see DevAuthService), react
        // to it here rather than pre-fetching permissions to guard routes client-side.
        if (!router.url.startsWith('/access')) router.navigateByUrl('/access');
        break;
      case err.status >= 500:
        toast.error('Serverfehler. Bitte später erneut versuchen.');
        break;
    }
    return throwError(() => err);
  };

  return auth.getAccessToken().pipe(
    take(1),
    catchError(() => of(null)),
    switchMap(token => {
      const req = token
        ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
        : request;

      return next(req).pipe(
        delay(0),
        catchError((err: HttpErrorResponse) => handleError(err)),
      );
    }),
  );
};
